import { usePostHog } from "@posthog/react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { CopyIcon } from "../../components/Icons/copy";
import { LinkIcon } from "../../components/Icons/link";
import { palette } from "../../styles/constants";

const CopiedIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.99999 13.6C11.0928 13.6 13.6 11.0928 13.6 8.00002C13.6 4.90723 11.0928 2.40002 7.99999 2.40002C4.9072 2.40002 2.39999 4.90723 2.39999 8.00002C2.39999 11.0928 4.9072 13.6 7.99999 13.6ZM10.595 7.095C10.8683 6.82163 10.8683 6.37842 10.595 6.10505C10.3216 5.83168 9.87839 5.83168 9.60502 6.10505L7.29999 8.41007L6.39497 7.50505C6.1216 7.23168 5.67839 7.23168 5.40502 7.50505C5.13165 7.77842 5.13165 8.22163 5.40502 8.495L6.80502 9.895C7.07839 10.1684 7.5216 10.1684 7.79497 9.895L10.595 7.095Z"
            fill="#22C55E"
        />
    </svg>
);

type GhostLinkProps = {
    href?: string;
    to?: string;
    copy?: string;
    target?: React.HTMLAttributeAnchorTarget;
    children?: React.ReactNode;
    showIndicator?: boolean;
    label?: string;
};

export const GhostLink = ({
    href,
    to,
    copy,
    target = "_blank",
    showIndicator = true,
    label,
    children,
}: GhostLinkProps) => {
    const { t } = useTranslation();
    const posthog = usePostHog();
    const [focused, setFocused] = useState(false);
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleCopy = () => {
        try {
            navigator.clipboard.writeText(copy!);
            posthog?.capture("ghost_link_copied", { label });
            setCopied(true);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.error(e);
        }
    };
    const handleLinkClick = () => {
        posthog?.capture("ghost_link_clicked", { label, to: href || to });
    };
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    if (to || (href && copy)) {
        // Link and/or copy: the value navigates, an optional separate copy
        // button carries its own tooltip so the link stays a plain click
        // target.
        return (
            <Wrapper>
                {to ? (
                    <RouterA to={to} onClick={handleLinkClick}>
                        {children || copy}
                    </RouterA>
                ) : (
                    <LinkA
                        href={href}
                        target={target}
                        onClick={handleLinkClick}
                    >
                        {children || copy}
                        {showIndicator && (
                            <Indicator>
                                <LinkIcon color={palette.primary[500]} />
                            </Indicator>
                        )}
                    </LinkA>
                )}
                {copy && (
                    <CopyButton
                        type="button"
                        aria-label={t("components.ghost_link.link_copy")}
                        onClick={handleCopy}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        onMouseEnter={() => setFocused(true)}
                        onMouseLeave={() => setFocused(false)}
                    >
                        <CopyIcon color={palette.neutral[900]} />
                        <Tooltip aria-hidden={!focused} data-hidden={!focused}>
                            {copied
                                ? t("components.ghost_link.copied")
                                : t("components.ghost_link.link", {
                                      context: "copy",
                                  })}
                            {copied && <CopiedIcon />}
                        </Tooltip>
                    </CopyButton>
                )}
            </Wrapper>
        );
    }

    const Container = href ? A : Copy;
    return (
        <Wrapper
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onMouseEnter={() => setFocused(true)}
            onMouseLeave={() => setFocused(false)}
        >
            <Container
                {...(href ? { href, target, onClick: handleLinkClick } : {})}
                {...(copy
                    ? { onClick: handleCopy, role: "button", tabIndex: 0 }
                    : {})}
                onKeyDown={(e) => {
                    if (copy) {
                        if (e.key === "Enter" || e.key === " ") {
                            handleCopy();
                        }
                    }
                }}
            >
                {children || copy || href?.replace(/^https?:\/\//, "")}

                {showIndicator && (
                    <Indicator>
                        {copy ? (
                            <CopyIcon color={palette.neutral[900]} />
                        ) : (
                            <LinkIcon color={palette.neutral[900]} />
                        )}
                    </Indicator>
                )}
            </Container>
            <Tooltip
                aria-hidden={!focused}
                data-hidden={!focused}
                {...(href
                    ? { href, target, as: "a", onClick: handleLinkClick }
                    : {})}
                {...(copy
                    ? { onClick: handleCopy, role: "button", tabIndex: -1 }
                    : {})}
            >
                {copied
                    ? t("components.ghost_link.copied")
                    : t("components.ghost_link.link", {
                          context: copy ? "copy" : "open",
                      })}
                {copied ? (
                    <CopiedIcon />
                ) : (
                    !copy && <LinkIcon color={palette.white} />
                )}
            </Tooltip>
        </Wrapper>
    );
};

const Wrapper = styled.div.attrs({
    className: "ghost-link",
})`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    position: relative;
`;

const Indicator = styled.div.attrs({
    className: "ghost-link-indicator",
})`
    display: inline-flex;
    flex-direction: column;
    align-self: center;
    padding-left: 4px;
`;

const linkStyle = css`
    text-decoration: none;
    cursor: pointer;
    position: relative;
    &:hover {
        text-decoration: underline;
    }
    &:focus-visible {
        outline: 2px solid #00f;
        outline-offset: 2px;
        border-radius: 1px;
    }
    display: inline-flex;
`;

const A = styled.a`
    ${linkStyle}
    color: inherit;
`;

const LinkA = styled.a`
    ${linkStyle}
    color: ${palette.primary[500]};
    &:active {
        color: ${palette.primary[700]};
    }
`;

const RouterA = styled(Link)`
    ${linkStyle}
    color: ${palette.primary[500]};
    &:active {
        color: ${palette.primary[700]};
    }
`;

const Copy = styled.span`
    cursor: pointer;
    color: inherit;
    position: relative;
    &:hover {
        text-decoration: underline;
        & > * {
            text-decoration: underline;
        }
    }
    &:focus-visible {
        outline: 2px solid #00f;
        outline-offset: 2px;
        border-radius: 1px;
    }
    display: inline-flex;
`;

const CopyButton = styled.button`
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: pointer;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    &:focus-visible {
        outline: 2px solid #00f;
        outline-offset: 2px;
        border-radius: 1px;
    }
`;

const Tooltip = styled.div`
    &[data-hidden="true"] {
        opacity: 0;
        transform: translateX(-50%) translateY(5px);
        visibility: hidden;
    }
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(0px);
    background-color: #000;
    color: #fff;
    z-index: 10;
    transition: all 0.2s ease;
    padding: 4px 8px;
    white-space: nowrap;
    border-radius: 3px;
    font-size: 12px;
    line-height: 20px;
    font-weight: 500px;
    text-align: center;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    &::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(-20%);
        border: 6px solid transparent;
        border-top: 6px solid #000;
    }
`;
